/**
 * Locale-aware formatting, on `Intl` and nothing else.
 *
 * Every function takes the locale explicitly and defaults to the *active*
 * locale - a module-level value the page-owning `I18nProvider` sets
 * (`applyToDocument`), `en-IN` until it does. That default is what lets
 * `lib/money.ts` keep its one-argument signatures while following the
 * dashboard's language.
 *
 * Currency is always passed separately and never derived from the language:
 * a Hindi-speaking merchant selling in USD sees `$1,000.00` written the Hindi
 * way, not rupees.
 *
 * `Intl` constructors are comparatively slow, and these run in table cells, so
 * formatters are cached by locale and options.
 */

export const DEFAULT_LOCALE = 'en-IN';

let activeLocale = DEFAULT_LOCALE;

/** The locale formatting falls back to when a call does not name one. */
export function getActiveLocale(): string {
  return activeLocale;
}

export function setActiveLocale(locale: string | null | undefined): void {
  activeLocale = locale || DEFAULT_LOCALE;
}

const EMPTY = '—';

const cache = new Map<string, unknown>();

function cached<T>(kind: string, locale: string, options: object | undefined, create: (locale: string) => T): T {
  const key = `${kind}|${locale}|${options ? JSON.stringify(options) : ''}`;
  let formatter = cache.get(key) as T | undefined;
  if (!formatter) {
    try {
      formatter = create(locale);
    } catch {
      // An unknown locale tag should degrade to the default, not crash a page.
      formatter = create(DEFAULT_LOCALE);
    }
    cache.set(key, formatter);
  }
  return formatter;
}

export function numberFormatter(locale: string, options?: Intl.NumberFormatOptions): Intl.NumberFormat {
  return cached('number', locale, options, (tag) => new Intl.NumberFormat(tag, options));
}

export function dateTimeFormatter(locale: string, options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  return cached('date', locale, options, (tag) => new Intl.DateTimeFormat(tag, options));
}

function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const number = typeof value === 'string' ? Number(value) : value;
  return Number.isFinite(number) ? number : null;
}

function toDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatNumber(
  value: string | number | null | undefined,
  locale: string = activeLocale,
  options?: Intl.NumberFormatOptions,
): string {
  const number = toNumber(value);
  return number === null ? EMPTY : numberFormatter(locale, options).format(number);
}

/**
 * An amount in a currency. Amounts usually arrive as decimal strings and are
 * only turned into a number here, at the point of display.
 */
export function formatMoney(
  amount: string | number | null | undefined,
  currency: string = 'INR',
  locale: string = activeLocale,
): string {
  const number = toNumber(amount);
  if (number === null) return EMPTY;
  return numberFormatter(locale, { style: 'currency', currency }).format(number);
}

export const DATE_OPTIONS: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };

export const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
};

export function formatDate(
  value: string | number | Date | null | undefined,
  locale: string = activeLocale,
  options: Intl.DateTimeFormatOptions = DATE_OPTIONS,
): string {
  const date = toDate(value);
  return date ? dateTimeFormatter(locale, options).format(date) : EMPTY;
}

export function formatDateTime(
  value: string | number | Date | null | undefined,
  locale: string = activeLocale,
  options: Intl.DateTimeFormatOptions = DATE_TIME_OPTIONS,
): string {
  return formatDate(value, locale, options);
}

const RELATIVE_UNITS: ReadonlyArray<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['week', 7 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
  ['second', 1],
];

/** "3 days ago", "in 2 hours", "yesterday" - relative to `now`. */
export function formatRelativeTime(
  value: string | number | Date | null | undefined,
  locale: string = activeLocale,
  now: number | Date = Date.now(),
): string {
  const date = toDate(value);
  if (!date) return EMPTY;
  const seconds = Math.round((date.getTime() - (now instanceof Date ? now.getTime() : now)) / 1000);
  const [unit, size] = RELATIVE_UNITS.find(([, span]) => Math.abs(seconds) >= span) ?? ['second', 1];
  const formatter = cached(
    'relative',
    locale,
    { numeric: 'auto' },
    (tag) => new Intl.RelativeTimeFormat(tag, { numeric: 'auto' }),
  );
  return formatter.format(Math.round(seconds / size), unit);
}

/** "Red, Green and Blue" in the locale's own list grammar. */
export function formatList(
  items: readonly string[],
  locale: string = activeLocale,
  type: Intl.ListFormatType = 'conjunction',
): string {
  const formatter = cached('list', locale, { type }, (tag) => new Intl.ListFormat(tag, { type }));
  return formatter.format(items);
}

/** The formatters bound to one locale - what the i18n context hands out. */
export interface Formatters {
  locale: string;
  number: (value: string | number | null | undefined, options?: Intl.NumberFormatOptions) => string;
  money: (amount: string | number | null | undefined, currency?: string) => string;
  date: (value: string | number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => string;
  dateTime: (value: string | number | Date | null | undefined, options?: Intl.DateTimeFormatOptions) => string;
  relativeTime: (value: string | number | Date | null | undefined, now?: number | Date) => string;
  list: (items: readonly string[], type?: Intl.ListFormatType) => string;
}

export function createFormatters(locale: string): Formatters {
  return {
    locale,
    number: (value, options) => formatNumber(value, locale, options),
    money: (amount, currency) => formatMoney(amount, currency, locale),
    date: (value, options) => formatDate(value, locale, options),
    dateTime: (value, options) => formatDateTime(value, locale, options),
    relativeTime: (value, now) => formatRelativeTime(value, locale, now),
    list: (items, type) => formatList(items, locale, type),
  };
}
